import React, { useState } from 'react';
import styled from '@emotion/styled';
import { SearchResult } from '../types';
import { MarkdownContent } from './MarkdownContent';
import { cleanText } from '../utils/textUtils';

interface SourcesPanelProps {
  sources: SearchResult[];
  loading: boolean;
}

const Container = styled.div<{ isCollapsed: boolean }>`
  width: ${props => props.isCollapsed ? '48px' : '100%'};
  max-width: ${props => props.isCollapsed ? '48px' : '380px'};
  height: fit-content;
  max-height: 85vh;
  overflow-y: ${props => props.isCollapsed ? 'hidden' : 'auto'};
  overflow-x: hidden;
  position: sticky;
  top: 20px;
  transition: width 0.3s ease, max-width 0.3s ease;
  background: ${props => props.isCollapsed ? 'transparent' : 'var(--color-background)'};
  border: ${props => props.isCollapsed ? 'none' : `var(--border-width) var(--border-style) var(--color-border)`};
  padding: ${props => props.isCollapsed ? '0' : 'var(--gap-md)'};
  border-radius: var(--radius-md);
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 3px;
    transition: background 0.2s ease;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--color-text);
    opacity: 0.3;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gap-lg);
  gap: var(--gap-sm);
`;

const Title = styled.h3<{ isCollapsed: boolean }>`
  margin: 0;
  color: var(--color-text);
  font-family: var(--font-heading);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold, var(--font-weight-bold));
  letter-spacing: var(--letter-spacing-tight);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${props => props.isCollapsed ? 0 : 1};
  transition: opacity 0.3s ease;
`;

const ToggleButton = styled.button<{ isCollapsed: boolean }>`
  background: ${props => props.isCollapsed ? 'var(--color-surface)' : 'transparent'};
  border: ${props => props.isCollapsed ? `var(--border-width) var(--border-style) var(--color-border)` : 'none'};
  color: var(--color-text);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;
  position: relative;
  width: ${props => props.isCollapsed ? '48px' : 'auto'};
  height: ${props => props.isCollapsed ? '48px' : 'auto'};
  
  &:hover {
    background: var(--color-surface);
    border-color: var(--color-text);
    
    &::after {
      content: ${props => props.isCollapsed ? '"Expand sources"' : '"Collapse"'};
      position: absolute;
      ${props => props.isCollapsed ? 'left: 100%' : 'right: 100%'};
      margin-left: ${props => props.isCollapsed ? '8px' : '0'};
      margin-right: ${props => props.isCollapsed ? '0' : '8px'};
      white-space: nowrap;
      background: var(--color-surface);
      padding: 4px 8px;
      border-radius: var(--radius-sm);
      font-size: 12px;
      border: var(--border-width) var(--border-style) var(--color-border);
      z-index: 10;
    }
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const SourceCard = styled.a`
  display: block;
  padding: var(--gap-md);
  margin-bottom: var(--gap-md);
  background: var(--color-surface);
  border: var(--border-width) var(--border-style) var(--color-border);
  border-radius: var(--radius-sm);
  text-decoration: none;
  color: inherit;
  transition: var(--transition);
  position: relative;
  overflow: hidden;

  &:hover {
    background: var(--color-surface);
    border-color: var(--color-primary);
    transform: var(--hover-transform);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--color-primary);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
`;

const SourceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: var(--gap-sm);
`;

const SourceNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  font-family: var(--font-mono);
`;

const SourceTitle = styled.h4`
  margin: 0;
  color: var(--color-text);
  font-family: var(--font-heading);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  line-height: 1.3;
  letter-spacing: var(--letter-spacing-tight);
  transition: var(--transition);
  flex: 1;
`;

const SourceSnippet = styled.div`
  margin: 0 0 var(--gap-sm) 0;
  font-family: var(--font-body);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  opacity: 0.9;
  line-height: 1.6;
  letter-spacing: var(--letter-spacing-normal);
  
  /* Limit markdown content styling */
  p {
    margin: 0 0 0.5em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Prevent overly large elements in snippets */
  h1, h2, h3, h4, h5, h6 {
    font-size: 1em;
    margin: 0.5em 0;
  }
`;

const SourceFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--gap-sm);
  padding-top: var(--gap-sm);
  border-top: 1px solid var(--color-border);
`;

const SourceDomain = styled.div`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-normal);
`;

const SourceUrl = styled.div`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  color: var(--color-text);
  opacity: 0.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: var(--letter-spacing-normal);
  max-width: 200px;
`;

const LoadingMessage = styled.p`
  font-family: var(--font-body);
  color: var(--color-text-secondary);
  font-style: italic;
  font-size: var(--font-size-base);
  letter-spacing: var(--letter-spacing-normal);
`;

const ContentWrapper = styled.div<{ isCollapsed: boolean }>`
  opacity: ${props => props.isCollapsed ? 0 : 1};
  visibility: ${props => props.isCollapsed ? 'hidden' : 'visible'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const SourcesPanel: React.FC<SourcesPanelProps> = ({ sources, loading }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  console.log('SourcesPanel received:', { sources, loading, sourcesLength: sources.length });

  if (loading) {
    return (
      <Container isCollapsed={isCollapsed}>
        <Header>
          <Title isCollapsed={isCollapsed}>Sources</Title>
          <ToggleButton isCollapsed={isCollapsed} onClick={() => setIsCollapsed(!isCollapsed)}>
            {isCollapsed ? '>' : '<'}
          </ToggleButton>
        </Header>
        <ContentWrapper isCollapsed={isCollapsed}>
          <LoadingMessage>Searching for relevant sources...</LoadingMessage>
        </ContentWrapper>
      </Container>
    );
  }

  return (
    <Container isCollapsed={isCollapsed}>
      <Header>
        <Title isCollapsed={isCollapsed}>Sources ({sources.length})</Title>
        <ToggleButton isCollapsed={isCollapsed} onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? '>' : '<'}
        </ToggleButton>
      </Header>
      <ContentWrapper isCollapsed={isCollapsed}>
        {sources.length === 0 ? (
          <p>No sources found</p>
        ) : (
          sources.map((source, index) => {
            // Extract domain from URL
            const getDomain = (url: string) => {
              try {
                const urlObj = new URL(url);
                return urlObj.hostname.replace('www.', '');
              } catch {
                return url;
              }
            };
            
            return (
              <SourceCard
                key={index}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SourceHeader>
                  <SourceNumber>{index + 1}</SourceNumber>
                  <SourceTitle>{cleanText(source.title)}</SourceTitle>
                </SourceHeader>
                <SourceSnippet>
                  <MarkdownContent content={cleanText(source.snippet)} />
                </SourceSnippet>
                <SourceFooter>
                  <SourceDomain>{getDomain(source.url)}</SourceDomain>
                  <SourceUrl title={source.url}>{source.url}</SourceUrl>
                </SourceFooter>
              </SourceCard>
            );
          })
        )}
      </ContentWrapper>
    </Container>
  );
};

export default SourcesPanel;